import { z } from 'zod'
import { cleanDocument } from '~/helpers/documentNumber'
import { OptionalTextSchema } from '../shared/text'
import { AddressListSchema } from './address'

/**
 * Schemas do cliente logado.
 *
 * Contrato da API (CustomerController e CustomerService.SerializeCustomerDTO), travado em
 * tests/Glofi.API.Tests/Snapshots/Customers/update.shape.txt (o customer do auth/login, do auth/me e do cadastro tem o
 * mesmo formato, sem o `id` do telefone no login):
 *
 * | Rota | Verbo | Corpo | Resposta 200 |
 * | --- | --- | --- | --- |
 * | `customer/update` | PUT | `IUpdateCustomerRequest` (PF) | `{ message, customer }` |
 * | `customer/update-company-customer` | PUT | `IUpdateCompanyCustomerRequest` (PJ) | `{ message, customer }` |
 * | `customer/change-password` | PUT | `IChangePasswordRequest` | `{ message, customer }` |
 *
 * Erros: 400 `{ message, errors }` com `errors` no formato do ModelState (`{ ".Email": { errors: [...] } }`) ou
 * ErrorList; o `handleApiError` já normaliza os dois (campo sem o ponto inicial).
 */

/** Só os dígitos (telefone e CPF, como a API espera: o update corta o DDD com Substring). */
const digitsOnly = (value: string) => value.replace(/\D/g, '')

const text = OptionalTextSchema

// ===== RESPOSTA =====

export const TelephoneSchema = z.object({
    /** Só o cadastro, o update e a troca de senha mandam o id (sempre nulo hoje); o login não manda. */
    id: z.string().nullish(),
    /** "CellPhone" nos cadastros da loja. */
    type: text,
    /** DDD. */
    prefix: text,
    number: text,
})

/**
 * Cliente logado, fonte única: o `customer` da sessão (`AuthSessionSchema`, logins e update-token), do cadastro e das
 * respostas de update e change-password. É o que fica em `useAuthStore` (`data.customer`).
 */
export const CustomerSchema = z.object({
    /** PF: nome e sobrenome juntos; PJ: razão social. */
    name: text,
    email: z.string(),
    /** CPF ou CNPJ sem máscara; `''` depois do `clear-document` e no cliente do login social. */
    mainDocNumber: text,
    occupation: text,
    /** Tipo do cliente como a API grava (`PERSON`, `COMPANY`, `Pessoa` do login social e variações antigas). */
    type: text,
    /** Só PJ. */
    inscricaoEstadual: text,
    inscricaoMunicipal: text,
    telephones: z
        .array(TelephoneSchema)
        .nullish()
        .transform((value) => value ?? []),
    addresses: AddressListSchema.nullish().transform((value) => value ?? []),
})

/** `{ message, customer }` de update, update-company-customer e change-password. */
export const CustomerResponseSchema = z.object({
    message: z.string().nullish(),
    customer: CustomerSchema,
})

// ===== REQUISIÇÃO =====

/**
 * `customer/update` (PF). A senha só vai quando o cliente sem cadastro completo (login sem conta do carrinho) cria a
 * senha no EditUserModal: a API grava a senha quando `password` e `confirmPassword` vêm no corpo. A troca de senha do
 * cliente com cadastro é pelo change-password.
 */
export const UpdateCustomerRequestSchema = z.object({
    firstName: z.string(),
    lastName: z.string(),
    cpf: z.string().transform(digitsOnly),
    phone: z.string().transform(digitsOnly),
    email: z.string(),
    occupation: z.string(),
    password: z.string().optional(),
    confirmPassword: z.string().optional(),
})

/** `customer/update-company-customer` (PJ). CNPJ alfanumérico em maiúsculas, sem máscara. */
export const UpdateCompanyCustomerRequestSchema = z.object({
    corporateName: z.string(),
    cnpj: z.string().transform(cleanDocument),
    companyPhone: z.string().transform(digitsOnly),
    email: z.string(),
    occupation: z.string(),
    inscricaoMunicipal: z.string(),
    inscricaoEstadual: z.string(),
})

/** `customer/change-password`. O e-mail é o do cliente logado. */
export const ChangePasswordRequestSchema = z.object({
    email: z.string(),
    currentPassword: z.string(),
    newPassword: z.string(),
    newPasswordConfirm: z.string(),
})

// ===== TIPOS =====

export type ITelephone = z.infer<typeof TelephoneSchema>
export type ICustomer = z.infer<typeof CustomerSchema>
export type ICustomerResponse = z.infer<typeof CustomerResponseSchema>
export type IUpdateCustomerRequest = z.input<typeof UpdateCustomerRequestSchema>
export type IUpdateCompanyCustomerRequest = z.input<typeof UpdateCompanyCustomerRequestSchema>
export type IChangePasswordRequest = z.infer<typeof ChangePasswordRequestSchema>
